import { Component, HostListener, Input, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import {
  DynamicEmailTableComponent,
  DynamicEmailTableResult,
} from "../../components/dynamic-email-table/dynamic-email-table.component";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { SessionService } from "src/app/services/session.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NavigationService } from "src/app/services/navigation.service";
import { ActivatedRoute } from "@angular/router";
import { DashboardService } from "src/app/services/dashboard.service";
import { ApiService } from "src/app/services/api.service";
import { EmailService } from "src/app/services/email-service";
import { DeleteTemplateComponent } from "src/app/components/shared/secure-mail/delete-template/delete-template.component";

type SectionType = "header" | "table" | "attachments" | "footer";

interface BaseSection {
  id: string;
  type: SectionType;
}

type Section = BaseSection & { [key: string]: any };

type HeadingLevel = "p" | "h1" | "h2" | "h3";

@Component({
  selector: "app-email-template-designer",
  templateUrl: "./email-template-designer.component.html",
  styleUrls: ["./email-template-designer.component.scss"],
})
export class EmailTemplateDesignerComponent implements OnInit {
  @Input("moduleName") moduleName: any;
  @Input("moduleKey") moduleKey: any;
  @Input("clientKey") clientKey: any;

  sections: Section[] = [];
  activeTab: "builder" | "settings" | "preview" = "builder";
  templateName = "";
  emailSubject = "";
  previewHtml!: SafeHtml;
  newColor: any;
  isLoadingPreview = false;
  isSavingTemplate = false;
  isTemplateSaved = false; // Track if template has been saved as draft
  hasAttachmentSection = false; // Track if attachment section exists
  attachmentMode: "file" | "sql" = "file"; // Default mode for attachments

  // Brand filter properties
  brands: any[] = [];
  filteredBrands: any[] = [];
  selectedBrand: any = null;  // full brand object {brand_key, brand_name, …}
  brandDropdownOpen = false;
  brandSearchText = '';
  isLoadingBrands = false;

  // Template listing properties
  showTemplateList = false; // Show template list after brand selection
  showDesigner = false; // Show designer when creating/editing
  templates: any[] = []; // Filtered templates for display
  allTemplates: any[] = []; // All templates before filtering

  isLoadingTemplates = false;
  isEditMode = false; // Track if we're editing an existing template

  // Pagination
  paginationPageSize = 10;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 0;
  totalPages = 1;
  visiblePages: (number | string)[] = [1];
  private etdGridApi: any = null;
  currentTemplateKey: number | null = null; // Store the key of template being edited

  // AG Grid configuration
  columnDefs: any[] = [
    {
      headerName: "Brand Name",
      field: "brand_key",
      sortable: true,
      flex: 1,
      valueFormatter: (params: any) => {
        if (params.value) {
          return this.brands.find((brand) => brand.brand_key === params.value)
            ?.brand_name;
        }
        return "";
      },
    },
    {
      headerName: "Template Name",
      field: "template_name",
      sortable: true,
      flex: 2,
    },
    {
      headerName: "Email Subject",
      field: "email_subject",
      sortable: true,
      flex: 2,
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: (params: any) => {
        const container = document.createElement("div");
        container.style.display = "flex";
        container.style.gap = "12px";
        container.style.alignItems = "center";
        container.style.justifyContent = "start";
        container.style.height = "100%";
        container.style.padding = "8px 12px";

        // Edit button
        const editBtn = document.createElement("button");
        editBtn.setAttribute("data-action", "edit");
        editBtn.setAttribute("data-id", params.data.email_template_key);
        editBtn.style.background = "transparent";
        editBtn.style.border = "none";
        editBtn.style.cursor = "pointer";
        editBtn.style.padding = "8px";
        editBtn.style.display = "flex";
        editBtn.style.alignItems = "center";
        editBtn.style.justifyContent = "center";
        editBtn.style.borderRadius = "4px";
        editBtn.style.transition = "opacity 0.15s";
        editBtn.style.opacity = "0.5";
        editBtn.title = "Edit Template";
        editBtn.onmouseenter = () => (editBtn.style.opacity = "1");
        editBtn.onmouseleave = () => (editBtn.style.opacity = "0.5");
        editBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#1363df" style="pointer-events: none;">
            <path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/>
          </svg>
        `;

        // Delete button
        const deleteBtn = document.createElement("button");
        deleteBtn.setAttribute("data-action", "delete");
        deleteBtn.setAttribute("data-id", params.data.email_template_key);
        deleteBtn.style.background = "transparent";
        deleteBtn.style.border = "none";
        deleteBtn.style.cursor = "pointer";
        deleteBtn.style.padding = "8px";
        deleteBtn.style.display = "flex";
        deleteBtn.style.alignItems = "center";
        deleteBtn.style.justifyContent = "center";
        deleteBtn.style.borderRadius = "4px";
        deleteBtn.style.transition = "opacity 0.15s";
        deleteBtn.style.opacity = "0.5";
        deleteBtn.title = "Delete Template";
        deleteBtn.onmouseenter = () => (deleteBtn.style.opacity = "1");
        deleteBtn.onmouseleave = () => (deleteBtn.style.opacity = "0.5");
        deleteBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#e53e3e" style="pointer-events: none;">
            <path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"/>
          </svg>
        `;

        container.appendChild(editBtn);
        container.appendChild(deleteBtn);

        return container;
      },
      cellStyle: {
        padding: "0",
        overflow: "visible",
      },
      flex: 1,
      sortable: false,
    },
  ];

  defaultColDef = {
    resizable: true,
    sortable: true,
  };

  gridOptions: any = {
    onCellClicked: (event: any) => this.onGridCellClicked(event),
  };

  onGridReady(params: any): void {
    this.etdGridApi = params.api;
  }

  onPaginationChanged(): void {
    if (!this.etdGridApi) return;
    this.currentPage = this.etdGridApi.paginationGetCurrentPage();
    this.totalPages = this.etdGridApi.paginationGetTotalPages() || 1;
    this.visiblePages = this.getVisiblePages();
  }

  goToPage(page: number): void {
    if (!this.etdGridApi || page < 0 || page >= this.totalPages) return;
    this.etdGridApi.paginationGoToPage(page);
  }

  onPageSizeChange(size: number): void {
    this.paginationPageSize = size;
    if (this.etdGridApi) {
      this.etdGridApi.paginationSetPageSize(size);
    }
  }

  private getVisiblePages(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage + 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | string)[] = [];
    if (current <= 4) {
      pages.push(1, 2, 3, 4, 5, '...', total);
    } else if (current >= total - 3) {
      pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
    } else {
      pages.push(1, '...', current - 1, current, current + 1, '...', total);
    }
    return pages;
  }

  constructor(
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private sessionService: SessionService,
    private snackBar: MatSnackBar,
    private navigationService: NavigationService,
    private router: ActivatedRoute,
    private dashboardService: DashboardService,
    private apiService: ApiService,
    private emailService: EmailService
  ) {
    this.newColor = this.sessionService.getColorCodes();
    this.router.params.subscribe((p: any) => {
      this.moduleName = p["module"] || "";

      this.router.queryParams.subscribe((params: any) => {
        let decryptedParams = this.navigationService.decryptData(params);
        this.clientKey = decryptedParams["cl_key"];
        this.moduleKey = decryptedParams["md_key"];
        this.dashboardService.moduleKey = this.moduleKey;
      });
    });
  }

  ngOnInit(): void {
    // Load brands on page load
    this.loadBrands();
  }

  loadBrands(): void {
    this.isLoadingBrands = true;
    this.apiService.post("PSS/get-brand-filter", {
      client_key: this.clientKey,
      module_key: this.moduleKey,
    }).subscribe({
      next: (response: any) => {
        const items = Array.isArray(response) ? response : [];
        this.brands = items;
        this.filteredBrands = [...items];
        this.isLoadingBrands = false;
        if (!this.selectedBrand && items.length > 0) {
          this.selectedBrand = items[0];
          this.loadTemplates();
        }
      },
      error: () => {
        this.brands = [];
        this.filteredBrands = [];
        this.isLoadingBrands = false;
      },
    });
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    // Close brand dropdown on outside click
    this.brandDropdownOpen = false;

    // If click is inside any header/footer toolbar or settings button, do nothing
    const insideToolbarOrButton = !!target.closest(
      ".header-toolbar-popup," +
        ".footer-toolbar-popup," +
        ".header-settings-btn," +
        ".footer-settings-btn"
    );

    if (insideToolbarOrButton) {
      return;
    }

    // Otherwise close all header/footer toolbars
    this.sections.forEach((sec) => {
      if (sec.type === "header") {
        sec["_showHeaderToolbar"] = false;
      }
      if (sec.type === "footer") {
        sec["_showFooterToolbar"] = false;
      }
    });
  }

  toggleBrandDropdown(event: Event): void {
    event.stopPropagation();
    this.brandDropdownOpen = !this.brandDropdownOpen;
    if (this.brandDropdownOpen) {
      this.brandSearchText = '';
      this.filteredBrands = [...this.brands];
    }
  }

  onBrandSearch(searchText: string): void {
    this.brandSearchText = searchText;
    if (!searchText.trim()) {
      this.filteredBrands = [...this.brands];
      return;
    }
    const lower = searchText.toLowerCase().trim();
    this.filteredBrands = this.brands.filter(
      (b) => (b.brand_name || '').toLowerCase().includes(lower)
    );
  }

  // Load templates based on selected brand
  loadTemplates(): void {
    this.isLoadingTemplates = true;
    this.emailService
      .getExtractEmailTemplates(this.selectedBrand?.brand_key)
      .then((response: any) => {
        this.isLoadingTemplates = false;
        this.allTemplates = response || [];

        // Initialize filtered brands
        this.filteredBrands = [...this.brands];

        // Apply filter (first brand already selected in loadBrands)
        this.applyBrandFilterToTemplates();

        this.showTemplateList = true;
        this.showDesigner = false;
      })
      .catch((error: any) => {
        this.isLoadingTemplates = false;
        console.error("Error loading templates:", error);
        this.snackBar.open(
          "Failed to load templates. Please try again.",
          "Close",
          {
            duration: 3000,
            panelClass: ["error-snackbar"],
          }
        );
      });
  }

  selectBrand(brand: any): void {
    this.selectedBrand = brand;
    this.brandDropdownOpen = false;
    this.brandSearchText = '';
    this.filteredBrands = [...this.brands];
    this.loadTemplates();
  }

  private applyBrandFilterToTemplates(): void {
    if (this.selectedBrand?.brand_key) {
      this.templates = this.allTemplates.filter(
        (template) => template.brand_key === this.selectedBrand.brand_key
      );
    } else {
      this.templates = [...this.allTemplates];
    }
  }

  // Handle AG Grid cell clicks for edit/delete actions
  onGridCellClicked(event: any): void {
    const target = event.event.target;
    const action = target.getAttribute("data-action");
    const templateKey = target.getAttribute("data-id");

    if (action === "edit" && templateKey) {
      this.editTemplate(parseInt(templateKey));
    } else if (action === "delete" && templateKey) {
      this.deleteTemplate(parseInt(templateKey));
    }
  }

  // Create new email design
  createNewTemplate(): void {
    this.isEditMode = false;
    this.currentTemplateKey = null;
    this.showTemplateList = false;
    this.showDesigner = true;
    this.clearTemplate();
  }
  // Edit existing template
  editTemplate(templateKey: number): void {
    this.isLoadingTemplates = true;
    this.emailService
      .getEditEmailTemplate(templateKey)
      .then((response: any) => {
        this.isLoadingTemplates = false;
        if (response) {
          // Handle response - check if it's an array or single object
          const templateData = Array.isArray(response) ? response[0] : response;

          if (templateData) {
            // Set edit mode
            this.isEditMode = true;
            this.currentTemplateKey = templateKey;

            // Populate template data
            this.templateName = templateData.template_name || "";
            this.emailSubject = templateData.email_subject || "";

            // Parse and populate email body (sections)
            if (templateData.email_body) {
              try {
                const emailBody =
                  typeof templateData.email_body === "string"
                    ? JSON.parse(templateData.email_body)
                    : templateData.email_body;

                // console.log("Parsed email body:", emailBody);

                if (emailBody.sections) {
                  this.sections = emailBody.sections;

                  // Transform table sections from API format to component format
                  this.sections.forEach((sec) => {
                    if (sec.type === "table" && sec["config"]) {
                      const config = sec["config"];

                      // Transform config to dynamicConfig format
                      sec["dynamicConfig"] = {
                        placeholderName: config.placeholder || "",
                        displayTitle: config.title || "",
                        sqlQuery: config.sqlQuery || "",
                        secretKey: config.secretKey || "",
                        cols: config.cols || [],
                        products: config.products || [],
                        headerStyle: config.styles?.header
                          ? {
                              fontFamily: config.styles.header.fontFamily,
                              fontSize: config.styles.header.fontSize,
                              bgColor: config.styles.header.background,
                              textColor: config.styles.header.textColor,
                              fontWeight: config.styles.header.fontWeight,
                              fontStyle:
                                config.styles.header.fontStyle || "normal",
                              textAlign: config.styles.header.textAlign,
                              textDecoration:
                                config.styles.header.textDecoration || "none",
                            }
                          : undefined,
                        bodyStyle: config.styles?.body
                          ? {
                              fontFamily: config.styles.body.fontFamily,
                              fontSize: config.styles.body.fontSize,
                              bgColor: config.styles.body.background,
                              textColor: config.styles.body.textColor,
                              fontWeight:
                                config.styles.body.fontWeight || "normal",
                              fontStyle:
                                config.styles.body.fontStyle || "normal",
                              textAlign: config.styles.body.textAlign,
                              textDecoration:
                                config.styles.body.textDecoration || "none",
                            }
                          : undefined,
                      };

                      // Remove the old config property
                      delete sec["config"];
                    }
                  });

                  // console.log("Loaded sections:", this.sections);

                  // Log table sections specifically
                  const tableSections = this.sections.filter(
                    (s) => s.type === "table"
                  );
                  // console.log("Table sections found:", tableSections);
                  tableSections.forEach((sec, index) => {
                    // console.log(
                    //   `Table ${index} dynamicConfig:`,
                    //   sec["dynamicConfig"]
                    // );
                  });

                  // Check if there's an attachment section
                  this.hasAttachmentSection = this.sections.some(
                    (sec) => sec.type === "attachments"
                  );
                }
              } catch (error) {
                console.error("Error parsing email body:", error);
              }
            }

            // Show designer
            this.showTemplateList = false;
            this.showDesigner = true;
            this.isTemplateSaved = true; // Since it's an existing template

            this.snackBar.open("Template loaded for editing", "Ok", {
              duration: 2000,
              panelClass: ["success-snackbar"],
            });
          } else {
            this.snackBar.open("No template data found", "Close", {
              duration: 3000,
              panelClass: ["error-snackbar"],
            });
          }
        }
      })
      .catch((error: any) => {
        this.isLoadingTemplates = false;
        console.error("Error loading template for edit:", error);
        this.snackBar.open(
          "Failed to load template. Please try again.",
          "Close",
          {
            duration: 3000,
            panelClass: ["error-snackbar"],
          }
        );
      });
  }

  // Delete template
  deleteTemplate(templateKey: number): void {
    // Find the template from the templates array
    const template = this.templates.find(
      (t) => t.email_template_key === templateKey
    );
    const index = this.templates.findIndex(
      (t) => t.email_template_key === templateKey
    );

    // Open the delete confirmation dialog
    const dialogRef = this.dialog.open(DeleteTemplateComponent, {
      width: "40%",
      height: "35%",
      disableClose: false,
      panelClass: "du-custom-modalbox",
      data: { template, index }, // Pass the template and its index
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === "delete") {
        if (templateKey) {
          this.emailService
            .deleteTemplates(templateKey)
            .then((response: any) => {
              this.snackBar.open("Template deleted successfully", "Ok", {
                duration: 2000,
                panelClass: ["success-snackbar"],
              });
              // Reload templates
              this.loadTemplates();
            })
            .catch((error: any) => {
              console.error("Error deleting template:", error);
              this.snackBar.open(
                "Failed to delete template. Please try again.",
                "Close",
                {
                  duration: 3000,
                  panelClass: ["error-snackbar"],
                }
              );
            });
        } else {
          console.error("Template key is missing. Cannot delete template.");
          this.snackBar.open(
            "Template key is missing. Cannot delete template.",
            "Close",
            {
              duration: 3000,
              panelClass: ["error-snackbar"],
            }
          );
        }
      }
    });
  }

  // Go back to template list
  backToTemplateList(): void {
    this.showDesigner = false;
    this.showTemplateList = true;
    this.isEditMode = false;
    this.currentTemplateKey = null;
    this.loadTemplates();
  }

  goBack(): void {
    this.navigationService.goBack();
  }

  private uid(prefix = ""): string {
    return `${prefix}${Math.random().toString(36).substr(2, 9)}`;
  }

  setActiveTab(tab: "builder" | "settings" | "preview") {
    // Validate before switching to preview
    if (tab === "preview") {
      if (!this.templateName) {
        this.snackBar.open("Template name is required!", "Ok", {
          duration: 3000,
          panelClass: ["error-snackbar"],
        });
        return;
      }

      if (this.sections.length === 0) {
        this.snackBar.open(
          "Please add content to the template before previewing!",
          "Ok",
          {
            duration: 3000,
            panelClass: ["error-snackbar"],
          }
        );
        return;
      }

      if (!this.isTemplateSaved) {
        this.snackBar.open(
          "Please save the template as draft before previewing!",
          "Ok",
          {
            duration: 3000,
            panelClass: ["error-snackbar"],
          }
        );
        return;
      }

      this.activeTab = tab;
      this.loadPreviewFromApi();
    } else {
      this.activeTab = tab;
    }
  }

  /**
   * Calls the backend API to generate HTML preview from the template JSON
   */
  loadPreviewFromApi() {
    this.isLoadingPreview = true;
    const templateJson = this.getTemplateJson();

    // Create the same payload structure as saveTemplate API
    const payload: any = {
      template_name: this.templateName || "",
      email_subject: this.emailSubject || "",
      email_body: JSON.stringify(templateJson),
      template_desc: "",
      brand_key: this.selectedBrand?.brand_key || "",
      template_type: "E",
    };

    // If in edit mode, include the template key
    if (this.isEditMode && this.currentTemplateKey) {
      payload.email_template_key = this.currentTemplateKey.toString();
    }

    // Call the backend API endpoint
    this.apiService
      .post("api/EmailTemplate/preview-email-template", payload)
      .subscribe({
        next: (response: any) => {
          this.isLoadingPreview = false;
          if (response && response.email_body) {
            // Sanitize and set the HTML from backend response
            this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(
              response.email_body
            );
          } else {
            this.snackBar.open("Invalid response from server", "Ok", {
              duration: 3000,
              panelClass: ["error-snackbar"],
            });
            // Fallback to local generation
            const raw = this.generateEmailHtml();
            this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(raw);
          }
        },
        error: (error) => {
          this.isLoadingPreview = false;
          console.error("Error loading preview from API:", error);
          this.snackBar.open(
            "Failed to load preview from server. Using local preview.",
            "Ok",
            {
              duration: 3000,
              panelClass: ["error-snackbar"],
            }
          );
          // Fallback to local generation
          const raw = this.generateEmailHtml();
          this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(raw);
        },
      });
  }

  goToEmailExtracts() {
    let query: any = {
      tab_id: 0,
      cl_key: this.clientKey,
      md_key: this.moduleKey,
      ds_key: "20251202133154",
    };
    this.navigationService.routerNavigate(
      `dashboard/${this.moduleName}/${"direct"}/${"Email Extracts"}`,
      query
    );
  }

  saveTemplate() {
    if (!this.templateName) {
      this.snackBar.open("Template name is required!", "Ok", {
        duration: 3000,
        panelClass: ["error-snackbar"],
      });
      return;
    }

    if (this.sections.length === 0) {
      this.snackBar.open(
        "Please add content to the template before saving!",
        "Ok",
        {
          duration: 3000,
          panelClass: ["error-snackbar"],
        }
      );
      return;
    }

    this.isSavingTemplate = true;
    const payload = this.getTemplateJson();

    const formData = new FormData();
    formData.append("template_name", this.templateName || "");
    formData.append("email_subject", this.emailSubject || "");
    formData.append("email_body", JSON.stringify(payload) || "");
    formData.append("template_desc", "");
    formData.append("brand_key", this.selectedBrand?.brand_key || "");
    formData.append("template_type", "E");

    // If in edit mode, append the template key
    if (this.isEditMode && this.currentTemplateKey) {
      formData.append("email_template_key", this.currentTemplateKey.toString());
    }

    // Collect all attachment files from attachment section (only in file mode)
    let fileIndex = 0;
    for (const section of this.sections) {
      if (
        section.type === "attachments" &&
        section["mode"] === "file" &&
        section["fileObjects"]
      ) {
        const fileObjects = section["fileObjects"] || [];
        for (const fileObj of fileObjects) {
          if (fileObj.file) {
            formData.append(`attachment_${fileIndex}`, fileObj.file);
            fileIndex++;
          }
        }
      }
    }

    // Only append file count if files exist
    if (fileIndex > 0) {
      formData.append("attachment_count", fileIndex.toString());
    }

    // Use editTemplates for update, createNewTemplates for create
    const saveMethod = this.isEditMode
      ? this.emailService.editTemplates(formData)
      : this.emailService.createNewTemplates(formData);

    saveMethod
      .then((response) => {
        this.isSavingTemplate = false;
        this.isTemplateSaved = true; // Enable preview access after save
        const message = this.isEditMode
          ? "Template updated successfully!"
          : "Template saved successfully!";
        this.snackBar.open(message, "Ok", {
          duration: 3000,
          panelClass: ["success-snackbar"],
        });
      })
      .catch((error) => {
        console.error("Error saving template:", error);
        this.isSavingTemplate = false;
        const message = this.isEditMode
          ? "Failed to update template. Please try again."
          : "Failed to save template. Please try again.";
        this.snackBar.open(message, "Close", {
          duration: 3000,
          panelClass: ["error-snackbar"],
        });
      });
  }

  clearTemplate(response?: any) {
    // Reset all sections
    this.sections = [];

    // Reset template name to default
    this.templateName = "";

    // Reset email subject
    this.emailSubject = "";

    // Reset saved state
    this.isTemplateSaved = false;

    // Reset attachment section flag
    this.hasAttachmentSection = false;

    // Switch to builder tab
    this.activeTab = "builder";

    // Show confirmation message
    if (!response) {
      this.snackBar.open("Template cleared successfully!", "Ok", {
        duration: 2000,
        panelClass: ["success-snackbar"],
      });
    }
  }

  onSectionDrop(event: CdkDragDrop<Section[]>) {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.sections, event.previousIndex, event.currentIndex);
  }

  // Section management
  addHeader() {
    this.sections.push({
      id: this.uid("hdr_"),
      type: "header",
      title: "New header title",
      headerStyle: {
        heading: "p" as HeadingLevel,
        fontSize: "16px",
        fontWeight: "normal",
        fontStyle: "normal",
        textAlign: "left",
        textColor: "#000000",
        bgColor: "transparent",
        textDecoration: "none",
      },
    });
    this.activeTab = "builder";
  }

  // toggle the popup toolbar for a header section
  toggleHeaderToolbar(sec: Section) {
    sec["_showHeaderToolbar"] = !sec["_showHeaderToolbar"];
  }

  // style to apply directly to the input
  getHeaderInputStyle(sec: Section): { [k: string]: any } {
    const style = this.getHeaderStyle(sec); // reuses your existing helper

    return {
      "background-color": style.bgColor || "transparent",
      color: style.textColor || "#000000",
      "font-weight": style.fontWeight || "bold",
      "font-style": style.fontStyle || "normal",
      "text-align": style.textAlign || "left",
      "text-decoration": style.textDecoration || "none",
      "font-size": style.fontSize || "20px",
    };
  }

  addFooter() {
    this.sections.push({
      id: this.uid("ftr_"),
      type: "footer",
      content: "Footer content — address / unsubscribe info",
      footerStyle: {
        heading: "p" as HeadingLevel,
        fontSize: "13px",
        fontWeight: "normal",
        fontStyle: "normal",
        textAlign: "left",
        textColor: "#666666",
        bgColor: "transparent",
        textDecoration: "none",
      },
    });
    this.activeTab = "builder";
  }

  // FOOTER STYLE HELPERS
  toggleFooterToolbar(sec: Section) {
    sec["_showFooterToolbar"] = !sec["_showFooterToolbar"];
  }

  private ensureFooterStyle(sec: Section): any {
    if (!sec["footerStyle"]) {
      sec["footerStyle"] = {
        heading: "p",
        fontSize: "13px",
        fontWeight: "normal",
        fontStyle: "normal",
        textAlign: "left",
        textColor: "#666666",
        bgColor: "transparent",
        textDecoration: "none",
      };
    }
    return sec["footerStyle"];
  }

  private getFooterStyle(sec: Section): any {
    return this.ensureFooterStyle(sec);
  }

  getFooterHeading(sec: Section): HeadingLevel {
    const style = this.ensureFooterStyle(sec);
    return (style.heading as HeadingLevel) || "p";
  }

  onFooterHeadingChange(sec: Section, value: HeadingLevel) {
    const style = this.ensureFooterStyle(sec);
    style.heading = value;

    // map heading to font size
    if (value === "h1") style.fontSize = "18px";
    else if (value === "h2") style.fontSize = "16px";
    else if (value === "h3") style.fontSize = "14px";
    else style.fontSize = "13px";
  }

  isFooterBold(sec: Section): boolean {
    const style = this.getFooterStyle(sec);
    return style.fontWeight === "bold";
  }

  toggleFooterBold(sec: Section) {
    const style = this.getFooterStyle(sec);
    style.fontWeight = style.fontWeight === "bold" ? "normal" : "bold";
  }

  isFooterItalic(sec: Section): boolean {
    const style = this.getFooterStyle(sec);
    return style.fontStyle === "italic";
  }

  toggleFooterItalic(sec: Section) {
    const style = this.getFooterStyle(sec);
    style.fontStyle = style.fontStyle === "italic" ? "normal" : "italic";
  }

  isFooterUnderline(sec: Section): boolean {
    const style = this.getFooterStyle(sec);
    return this.hasDecoration(style, "underline");
  }

  isFooterStrike(sec: Section): boolean {
    const style = this.getFooterStyle(sec);
    return this.hasDecoration(style, "line-through");
  }

  toggleFooterUnderline(sec: Section) {
    const style = this.getFooterStyle(sec);
    const underline = !this.isFooterUnderline(sec);
    const strike = this.isFooterStrike(sec);
    style.textDecoration = this.buildTextDecoration(underline, strike);
  }

  toggleFooterStrike(sec: Section) {
    const style = this.getFooterStyle(sec);
    const underline = this.isFooterUnderline(sec);
    const strike = !this.isFooterStrike(sec);
    style.textDecoration = this.buildTextDecoration(underline, strike);
  }

  isFooterAlign(sec: Section, dir: "left" | "center" | "right"): boolean {
    const style = this.getFooterStyle(sec);
    return (style.textAlign || "left") === dir;
  }

  setFooterAlign(sec: Section, dir: "left" | "center" | "right") {
    const style = this.getFooterStyle(sec);
    style.textAlign = dir;
  }

  getFooterTextColor(sec: Section): string {
    const style = this.getFooterStyle(sec);
    return style.textColor || "#666666";
  }

  getFooterBgColor(sec: Section): string {
    const style = this.getFooterStyle(sec);
    return style.bgColor || "transparent";
  }

  onFooterTextColorChange(sec: Section, event: Event) {
    const style = this.getFooterStyle(sec);
    const value = (event.target as HTMLInputElement).value;
    style.textColor = value;
  }

  onFooterBgColorChange(sec: Section, event: Event) {
    const style = this.getFooterStyle(sec);
    const value = (event.target as HTMLInputElement).value;
    style.bgColor = value;
  }

  getFooterInputStyle(sec: Section): { [k: string]: any } {
    const style = this.getFooterStyle(sec);
    return {
      "background-color": style.bgColor || "transparent",
      color: style.textColor || "#666666",
      "font-weight": style.fontWeight || "normal",
      "font-style": style.fontStyle || "normal",
      "text-align": style.textAlign || "left",
      "text-decoration": style.textDecoration || "none",
      "font-size": style.fontSize || "13px",
    };
  }

  addTable() {
    this.sections.push({
      id: this.uid("tbl_"),
      type: "table",
    });
    this.activeTab = "builder";
  }

  addAttachment() {
    if (this.hasAttachmentSection) {
      return; // Prevent adding multiple attachment sections
    }

    this.hasAttachmentSection = true;
    this.sections.push({
      id: this.uid("att_"),
      type: "attachments",
      mode: this.attachmentMode,
      // For file mode
      files: [], // Array to store file metadata
      fileObjects: [], // Array to store actual File objects
      // For SQL mode
      sqlQueries: [
        {
          id: this.uid("sql_"),
          sqlQuery: "",
          secretKey: "",
          connectionString: "",
        },
      ],
    });
    this.activeTab = "builder";
  }

  // Add a new SQL query entry to the attachment section
  addSqlQuery(sec: Section) {
    if (sec.type !== "attachments") return;

    const sqlQueries = sec["sqlQueries"] || [];
    sqlQueries.push({
      id: this.uid("sql_"),
      sqlQuery: "",
      secretKey: "",
      connectionString: "",
    });
    sec["sqlQueries"] = sqlQueries;
  }

  // Remove a SQL query entry from the attachment section
  removeSqlQuery(sec: Section, queryId: string) {
    if (sec.type !== "attachments") return;

    const sqlQueries = sec["sqlQueries"] || [];
    sec["sqlQueries"] = sqlQueries.filter((q: any) => q.id !== queryId);
  }

  // Switch attachment mode
  onAttachmentModeChange(sec: Section, mode: "file" | "sql") {
    if (sec.type !== "attachments") return;
    sec["mode"] = mode;
    this.attachmentMode = mode;
  }

  executeAttachmentSql(sec: Section) {
    // Guard: all fields required (same as button disabled condition)
    if (!sec["sqlQuery"] || !sec["secretKey"] || !sec["connectionString"]) {
      return;
    }

    // TODO: call real API here if needed.
    // For now we just mark as executed.
    sec["sqlExecuted"] = true;
  }

  removeSection(index: number) {
    if (index >= 0 && index < this.sections.length) {
      const section = this.sections[index];
      if (section.type === "attachments") {
        this.hasAttachmentSection = false;
      }
      this.sections.splice(index, 1);
    }
  }

  // TABLE SETTINGS DIALOG
  openTableSettings(sec: Section) {
    if (sec.type !== "table") return;

    const existing = sec["dynamicConfig"] as
      | DynamicEmailTableResult
      | undefined;

    // console.log("Opening table settings for section:", sec);
    // console.log("Existing dynamicConfig:", existing);

    const dialogData = {
      mode: existing ? "edit" : "new",
      placeholderName: existing?.placeholderName,
      displayTitle: existing?.displayTitle,
      sqlQuery: existing?.sqlQuery,
      secretKey: existing?.secretKey,
      cols: existing?.cols,
      products: existing?.products,
      headerStyle: existing?.headerStyle,
      bodyStyle: existing?.bodyStyle,
    };

    // console.log("Dialog data being passed:", dialogData);

    const dialogRef = this.dialog.open(DynamicEmailTableComponent, {
      width: "900px",
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result?: DynamicEmailTableResult) => {
      if (!result) return;
      // console.log("Dialog closed with result:", result);
      sec["dynamicConfig"] = result;
    });
  }

  // ATTACHMENTS
  onAttachmentFilesSelected(sec: Section, event: Event) {
    if (sec.type !== "attachments") return;
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const filesArr = sec["files"] || [];
    const fileObjectsArr = sec["fileObjects"] || [];

    for (let i = 0; i < input.files.length; i++) {
      const f = input.files[i];
      const fileId = this.uid("file_");

      // Store metadata for display
      filesArr.push({
        id: fileId,
        name: f.name,
        size: f.size,
      });

      // Store actual File object for upload
      fileObjectsArr.push({
        id: fileId,
        file: f,
      });
    }

    sec["files"] = filesArr;
    sec["fileObjects"] = fileObjectsArr;
    input.value = "";
  }

  removeAttachmentFile(sec: Section, fileId: string) {
    if (sec.type !== "attachments") return;
    const files = sec["files"] || [];
    const fileObjects = sec["fileObjects"] || [];

    sec["files"] = files.filter((f: any) => f.id !== fileId);
    sec["fileObjects"] = fileObjects.filter((f: any) => f.id !== fileId);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  // HEADER STYLE HELPERS
  private ensureHeaderStyle(sec: Section): any {
    if (!sec["headerStyle"]) {
      sec["headerStyle"] = {
        heading: "p",
        fontSize: "16px",
        fontWeight: "normal",
        fontStyle: "normal",
        textAlign: "left",
        textColor: "#000000",
        bgColor: "transparent",
        textDecoration: "none",
      };
    }
    return sec["headerStyle"];
  }

  getHeaderHeading(sec: Section): HeadingLevel {
    const style = this.ensureHeaderStyle(sec);
    return (style.heading as HeadingLevel) || "p";
  }

  onHeaderHeadingChange(sec: Section, value: HeadingLevel) {
    const style = this.ensureHeaderStyle(sec);
    style.heading = value;

    // map heading to font size
    if (value === "h1") style.fontSize = "26px";
    else if (value === "h2") style.fontSize = "22px";
    else if (value === "h3") style.fontSize = "18px";
    else style.fontSize = "16px";
  }

  private getHeaderStyle(sec: Section): any {
    return this.ensureHeaderStyle(sec);
  }

  isHeaderBold(sec: Section): boolean {
    const style = this.getHeaderStyle(sec);
    return style.fontWeight === "bold";
  }

  toggleHeaderBold(sec: Section) {
    const style = this.getHeaderStyle(sec);
    style.fontWeight = style.fontWeight === "bold" ? "normal" : "bold";
  }

  isHeaderItalic(sec: Section): boolean {
    const style = this.getHeaderStyle(sec);
    return style.fontStyle === "italic";
  }

  toggleHeaderItalic(sec: Section) {
    const style = this.getHeaderStyle(sec);
    style.fontStyle = style.fontStyle === "italic" ? "normal" : "italic";
  }

  private hasDecoration(
    style: any,
    deco: "underline" | "line-through"
  ): boolean {
    const textDeco = (style.textDecoration || "").toString();
    return textDeco.includes(deco);
  }

  isHeaderUnderline(sec: Section): boolean {
    const style = this.getHeaderStyle(sec);
    return this.hasDecoration(style, "underline");
  }

  isHeaderStrike(sec: Section): boolean {
    const style = this.getHeaderStyle(sec);
    return this.hasDecoration(style, "line-through");
  }

  private buildTextDecoration(underline: boolean, strike: boolean): string {
    const parts: string[] = [];
    if (underline) parts.push("underline");
    if (strike) parts.push("line-through");
    return parts.length ? parts.join(" ") : "none";
  }

  toggleHeaderUnderline(sec: Section) {
    const style = this.getHeaderStyle(sec);
    const underline = !this.isHeaderUnderline(sec);
    const strike = this.isHeaderStrike(sec);
    style.textDecoration = this.buildTextDecoration(underline, strike);
  }

  toggleHeaderStrike(sec: Section) {
    const style = this.getHeaderStyle(sec);
    const underline = this.isHeaderUnderline(sec);
    const strike = !this.isHeaderStrike(sec);
    style.textDecoration = this.buildTextDecoration(underline, strike);
  }

  isHeaderAlign(sec: Section, dir: "left" | "center" | "right"): boolean {
    const style = this.getHeaderStyle(sec);
    return (style.textAlign || "left") === dir;
  }

  setHeaderAlign(sec: Section, dir: "left" | "center" | "right") {
    const style = this.getHeaderStyle(sec);
    style.textAlign = dir;
  }

  getHeaderTextColor(sec: Section): string {
    const style = this.getHeaderStyle(sec);
    return style.textColor || "#000000";
  }

  getHeaderBgColor(sec: Section): string {
    const style = this.getHeaderStyle(sec);
    return style.bgColor || "transparent";
  }

  onHeaderTextColorChange(sec: Section, event: Event) {
    const style = this.getHeaderStyle(sec);
    const value = (event.target as HTMLInputElement).value;
    style.textColor = value;
  }

  onHeaderBgColorChange(sec: Section, event: Event) {
    const style = this.getHeaderStyle(sec);
    const value = (event.target as HTMLInputElement).value;
    style.bgColor = value;
  }

  getHeaderPreviewStyle(sec: Section): { [k: string]: any } {
    const style = this.getHeaderStyle(sec);
    return {
      "background-color": style.bgColor || "transparent",
      color: style.textColor || "#000000",
      "font-weight": style.fontWeight || "bold",
      "font-style": style.fontStyle || "normal",
      "text-align": style.textAlign || "left",
      "text-decoration": style.textDecoration || "none",
      "font-size": style.fontSize || "20px",
      padding: "8px 12px",
      "border-radius": "4px",
    };
  }

  // Preview / Export
  openPreview() {
    this.setActiveTab("preview");
  }

  getTemplateJson() {
    // Transform sections to API format
    const transformedSections: any[] = [];

    this.sections.forEach((s) => {
      if (s.type === "header") {
        // Transform header section
        const transformed: any = {
          id: s.id,
          type: s.type,
          title: s["title"] || "",
        };
        const headerStyle = s["headerStyle"] || {};
        transformed.style = {
          tag: headerStyle.heading || "p",
          fontSize: headerStyle.fontSize || "16px",
          fontWeight: headerStyle.fontWeight || "normal",
          textAlign: headerStyle.textAlign || "left",
          textColor: headerStyle.textColor || "#000000",
          background: headerStyle.bgColor || "transparent",
        };
        transformedSections.push(transformed);
      } else if (s.type === "table") {
        // Transform table section
        const transformed: any = {
          id: s.id,
          type: s.type,
        };
        const dynamicConfig = s["dynamicConfig"];
        if (dynamicConfig) {
          transformed.config = {
            placeholder: dynamicConfig.placeholderName || "",
            title: dynamicConfig.displayTitle || "",
            sqlQuery: dynamicConfig.sqlQuery || "",
            secretKey: dynamicConfig.secretKey || "",
            styles: {
              header: {},
              body: {},
            },
          };

          // Transform header styles
          if (dynamicConfig.headerStyle) {
            const hs = dynamicConfig.headerStyle;
            transformed.config.styles.header = {
              fontFamily: hs.fontFamily || "Arial",
              fontSize: hs.fontSize || "14px",
              background: hs.bgColor || "#f7f9fb",
              textColor: hs.textColor || "#000000",
              fontWeight: hs.fontWeight || "bold",
              textAlign: hs.textAlign || "left",
            };
          }

          // Transform body styles
          if (dynamicConfig.bodyStyle) {
            const bs = dynamicConfig.bodyStyle;
            transformed.config.styles.body = {
              fontFamily: bs.fontFamily || "Arial",
              fontSize: bs.fontSize || "14px",
              background: bs.bgColor || "#ffffff",
              textColor: bs.textColor || "#000000",
              textAlign: bs.textAlign || "left",
            };
          }
        }
        transformedSections.push(transformed);
      } else if (s.type === "attachments") {
        // Transform attachments section
        const mode = s["mode"] || "file";

        if (mode === "sql") {
          // For SQL mode, create separate sections for each SQL query
          const sqlQueries = s["sqlQueries"] || [];
          sqlQueries.forEach((query: any) => {
            // Only add if query has values
            if (query.sqlQuery || query.secretKey || query.connectionString) {
              transformedSections.push({
                id: query.id || this.uid("att_"),
                type: "attachments",
                sqlQuery: query.sqlQuery || "",
                secretKey: query.secretKey || "",
                connectionString: query.connectionString || "",
                executed: true,
              });
            }
          });
        }
        // For file mode, files are handled separately in FormData, not in JSON
      } else if (s.type === "footer") {
        // Transform footer section
        const transformed: any = {
          id: s.id,
          type: s.type,
          content: s["content"] || "",
        };
        const footerStyle = s["footerStyle"] || {};
        transformed.style = {
          tag: footerStyle.heading || "p",
          fontSize: footerStyle.fontSize || "13px",
          textColor: footerStyle.textColor || "#666666",
          textAlign: footerStyle.textAlign || "left",
          background: footerStyle.bgColor || "transparent",
        };
        transformedSections.push(transformed);
      }
    });

    return {
      createdAt: new Date().toISOString(),
      settings: {
        templateName: this.templateName,
      },
      sections: transformedSections,
    };
  }

  downloadJson() {
    const data = JSON.stringify(this.getTemplateJson(), null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "email-template.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  generateEmailHtml(): string {
    let html = `<div style="font-family:Arial,Helvetica,sans-serif;color:#222;">`;

    // Top template header
    html += `<div style="padding:12px 0;border-bottom:1px solid #eee;margin-bottom:12px;">
      <h1 style="margin:0;">${this.escape(this.templateName)}</h1>
    </div>`;

    for (const s of this.sections) {
      if (s.type === "header") {
        // ---------------- HEADER ----------------
        const title = s["title"] || "";
        const hs = (s["headerStyle"] as any) || {};

        const bg = hs.bgColor || "transparent";
        const textColor = hs.textColor || "#000000";
        const fontSize = hs.fontSize || "16px";
        const fontWeight = hs.fontWeight || "normal";
        const fontStyle = hs.fontStyle || "normal";
        const textAlign = hs.textAlign || "left";
        const textDecoration = hs.textDecoration || "none";

        html += `
        <div style="padding:8px 0;background:${bg};">
          <div
            style="
              margin:0;
              color:${textColor};
              font-size:${fontSize};
              font-weight:${fontWeight};
              font-style:${fontStyle};
              text-align:${textAlign};
              text-decoration:${textDecoration};
            "
          >
            ${this.escape(title)}
          </div>
        </div>
      `;
      } else if (s.type === "table") {
        // ---------------- TABLE ----------------
        const cfg = s["dynamicConfig"] as DynamicEmailTableResult | undefined;

        if (!cfg || !cfg.cols?.length || !cfg.products?.length) {
          html += `<div style="margin:12px 0;color:#888;">Data table not configured.</div>`;
          continue;
        }

        // headerStyle from DynamicEmailTableResult
        const hs = cfg.headerStyle || {};
        const headBg = hs.bgColor || "#f7f9fb";
        const headTextColor = hs.textColor || "#000000";
        const headFontFamily = hs.fontFamily || "Arial, sans-serif";
        const headFontSize = hs.fontSize || "14px";
        const headFontWeight = hs.fontWeight || "bold";
        const headFontStyle = hs.fontStyle || "normal";
        const headTextAlign = hs.textAlign || "left";
        const headTextDecoration = hs.textDecoration || "none";

        // bodyStyle from DynamicEmailTableResult
        const bs = cfg.bodyStyle || {};
        const bodyBg = bs.bgColor || "#ffffff";
        const bodyTextColor = bs.textColor || "#000000";
        const bodyFontFamily = bs.fontFamily || "Arial, sans-serif";
        const bodyFontSize = bs.fontSize || "14px";
        const bodyFontWeight = bs.fontWeight || "normal";
        const bodyFontStyle = bs.fontStyle || "normal";
        const bodyTextAlign = bs.textAlign || "left";
        const bodyTextDecoration = bs.textDecoration || "none";

        html += `<div style="margin:12px 0;">`;

        if (cfg.displayTitle) {
          html += `<div style="font-weight:bold;margin-bottom:4px;">${this.escape(
            cfg.displayTitle
          )}</div>`;
        }

        html += `<table border="1" cellpadding="8" cellspacing="0" style="border-collapse:collapse;width:100%;">`;

        // table header
        html += `<tr style="background:${headBg};">`;
        for (const c of cfg.cols) {
          html += `
          <th
            style="
              text-align:${headTextAlign};
              border:1px solid #ddd;
              padding:8px;
              color:${headTextColor};
              font-family:${headFontFamily};
              font-size:${headFontSize};
              font-weight:${headFontWeight};
              font-style:${headFontStyle};
              text-decoration:${headTextDecoration};
            "
          >
            ${this.escape(c.header)}
          </th>
        `;
        }
        html += `</tr>`;

        // table body
        for (const row of cfg.products) {
          html += `<tr>`;
          for (const c of cfg.cols) {
            const val = row ? (row as any)[c.field] : "";
            html += `
            <td
              style="
                border:1px solid #ddd;
                padding:8px;
                background:${bodyBg};
                color:${bodyTextColor};
                font-family:${bodyFontFamily};
                font-size:${bodyFontSize};
                font-weight:${bodyFontWeight};
                font-style:${bodyFontStyle};
                text-align:${bodyTextAlign};
                text-decoration:${bodyTextDecoration};
              "
            >
              ${this.escape(String(val ?? ""))}
            </td>
          `;
          }
          html += `</tr>`;
        }

        html += `</table></div>`;
        //   } else if (s.type === "attachments") {
        //     const sql = s["sqlQuery"] || "";
        //     const secretKey = s["secretKey"] || "";
        //     const conn = s["connectionString"] || "";
        //     const executed = !!s["sqlExecuted"];

        //     // if nothing is filled, still show a hint block
        //     if (!sql && !secretKey && !conn) {
        //       html += `
        //   <div style="margin:10px 0; padding:8px 10px; border:1px dashed #ddd; border-radius:4px; font-size:13px; color:#666;">
        //     <strong>Data source block:</strong> not configured yet.
        //   </div>
        // `;
        //     } else {
        //       html += `
        //   <div style="margin:10px 0; padding:10px 12px; border:1px solid #e3e6ec; border-radius:4px; font-size:13px;">
        //     <div style="font-weight:bold; margin-bottom:4px;">Data source configuration</div>
        //     <div><strong>SQL Query:</strong> ${this.escape(sql)}</div>
        //     <div><strong>Secret Key:</strong> ${this.escape(secretKey)}</div>
        //     <div><strong>Connection String:</strong> ${this.escape(conn)}</div>
        //     ${
        //       executed
        //         ? `<div style="margin-top:4px; color:#16a34a;">&#10003; SQL query executed</div>`
        //         : ""
        //     }
        //   </div>
        // `;
        //     }
      } else if (s.type === "attachments") {
        // No HTML output for attachments in preview
      } else if (s.type === "footer") {
        // ---------------- FOOTER ----------------
        const content = s["content"] || "";
        const fs = (s["footerStyle"] as any) || {};

        const textColor = fs.textColor || "#666666";
        const fontSize = fs.fontSize || "13px";
        const fontWeight = fs.fontWeight || "normal";
        const fontStyle = fs.fontStyle || "normal";
        const textAlign = fs.textAlign || "left";
        const textDecoration = fs.textDecoration || "none";
        const bg = fs.bgColor || "transparent";

        html += `
        <div style="margin-top:18px;border-top:1px solid #eee;padding-top:12px;background:${bg};">
          <div
            style="
              color:${textColor};
              font-size:${fontSize};
              font-weight:${fontWeight};
              font-style:${fontStyle};
              text-align:${textAlign};
              text-decoration:${textDecoration};
            "
          >
            ${this.escape(content)}
          </div>
        </div>
      `;
      }
    }

    html += `</div>`;
    return html;
  }

  convertToEmailFormat() {
    let emailBody = "";
    const attachments: any[] = [];

    // Generate email body HTML from sections
    for (const s of this.sections) {
      if (s.type === "header") {
        // ---------------- HEADER ----------------
        const title = s["title"] || "";
        const hs = (s["headerStyle"] as any) || {};

        const bg = hs.bgColor || "transparent";
        const textColor = hs.textColor || "#000000";
        const fontSize = hs.fontSize || "16px";
        const fontWeight = hs.fontWeight || "normal";
        const fontStyle = hs.fontStyle || "normal";
        const textAlign = hs.textAlign || "left";
        const textDecoration = hs.textDecoration || "none";

        emailBody += `\r\n        <div style='text-align:${textAlign}; background:${bg};'>\r\n            <p style='font-size:${fontSize}; font-weight:${fontWeight}; color:${textColor}; font-style:${fontStyle}; text-decoration:${textDecoration};'>\r\n                ${title}\r\n            </p>\r\n        </div>\r\n`;
      } else if (s.type === "table") {
        // ---------------- TABLE ----------------
        const cfg = s["dynamicConfig"] as DynamicEmailTableResult | undefined;

        if (!cfg || !cfg.cols?.length) {
          continue;
        }

        // headerStyle from DynamicEmailTableResult
        const hs = cfg.headerStyle || {};
        const headBg = hs.bgColor || "#f7f9fb";
        const headTextColor = hs.textColor || "#000000";
        const headFontSize = hs.fontSize || "14px";
        const headFontWeight = hs.fontWeight || "bold";
        const headFontStyle = hs.fontStyle || "normal";
        const headTextAlign = hs.textAlign || "left";
        const headTextDecoration = hs.textDecoration || "none";

        // bodyStyle from DynamicEmailTableResult
        const bs = cfg.bodyStyle || {};
        const bodyBg = bs.bgColor || "#ffffff";
        const bodyTextColor = bs.textColor || "#000000";
        const bodyFontSize = bs.fontSize || "14px";
        const bodyFontWeight = bs.fontWeight || "normal";
        const bodyFontStyle = bs.fontStyle || "normal";
        const bodyTextAlign = bs.textAlign || "left";
        const bodyTextDecoration = bs.textDecoration || "none";

        // Start table
        emailBody += `<table border='1' style='border-collapse:collapse; width:100%;'>\r\n    <thead style='background:${headBg};\r\n                  font-size:${headFontSize};\r\n                  font-weight:${headFontWeight};\r\n                  text-align:${headTextAlign};\r\n                  color:${headTextColor};'>\r\n        <tr>`;

        // Table headers
        for (const c of cfg.cols) {
          emailBody += `<th style='padding:6px;'>${c.header.toLowerCase()}</th>`;
        }
        emailBody += `</tr></thead><tbody>`;

        // Table body - use placeholder syntax
        emailBody += `<tr>\r\n`;
        for (const c of cfg.cols) {
          emailBody += `        <td style='padding:6px;\r\n                   font-size:${bodyFontSize};\r\n                   background:${bodyBg};\r\n                   color:${bodyTextColor};\r\n                   text-align:${bodyTextAlign};'>\r\n            {{${c.field}}}\r\n        </td>\r\n`;
        }
        emailBody += `</tr>`;

        emailBody += `</tbody></table>\r\n`;
      } else if (s.type === "footer") {
        // ---------------- FOOTER ----------------
        const content = s["content"] || "";
        const fs = (s["footerStyle"] as any) || {};

        const textColor = fs.textColor || "#666666";
        const fontSize = fs.fontSize || "13px";
        const fontWeight = fs.fontWeight || "normal";
        const fontStyle = fs.fontStyle || "normal";
        const textAlign = fs.textAlign || "left";
        const textDecoration = fs.textDecoration || "none";
        const bg = fs.bgColor || "transparent";

        emailBody += `        <div style='text-align:${textAlign}; background:${bg};'>\r\n            <p style='font-size:${fontSize}; color:${textColor}; font-weight:${fontWeight}; font-style:${fontStyle}; text-decoration:${textDecoration};'>\r\n                ${content}\r\n            </p>\r\n        </div>`;
      } else if (s.type === "attachments") {
        // ---------------- ATTACHMENTS ----------------
        const sqlQuery = s["sqlQuery"] || "";
        const secretKey = s["secretKey"] || "";
        const connectionString = s["connectionString"] || "";

        if (sqlQuery && secretKey && connectionString) {
          attachments.push({
            attachment_file_name: "",
            content_type: "",
            PreSignedUrl: "", // This will be populated by backend after SQL execution
            sqlQuery: sqlQuery,
            secretKey: secretKey,
            connectionString: connectionString,
          });
        }
      }
    }

    // Return the formatted email object
    return {
      Id: 0,
      template_name: this.templateName,
      email_subject: `Data Unveil - ${this.templateName}`,
      email_body: emailBody,
      Attachments: attachments,
    };
  }

  private escape(s: string): string {
    return (s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}
